class CountdownMessage extends window.BAO.CustomElement() {
  connectedCallback () {
    this.deadline = this.dataset.deadline
    this.message = this.dataset.message
    this.hideAfterEnd = this.dataset.hideAfterEnd === 'true'
    this.afterMessage = this.dataset.afterMessage
    this.separator = this.dataset.separator || ''

    this.countdownEls = null
    this.isVisible = false
    this.timerInterval = null

    this.showCountdown()
    this.setupIntersectionObserver()
  }

  setupIntersectionObserver () {
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting
        if (entry.isIntersecting) {
          this.startTimer()
        } else {
          this.stopTimer()
        }
      },
      { rootMargin: '50px' },
    )
    this.observer.observe(this)
  }

  stopTimer () {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  showCountdown () {
    this.els.message.element.innerHTML = this.message.replace(
      '[countdown]',
      this.getCountdownTemplate(),
    )
    this.setAttribute('aria-hidden', 'false')
  }

  getCountdownTemplate () {
    return `
      <span data-countdown-days></span>
      <span data-countdown-days-label>${window.theme.strings.countdown.days}</span>
      <span data-separator-days-hours></span>
      <span data-countdown-hours></span>
      <span data-countdown-hours-label>${window.theme.strings.countdown.hours}</span>
      <span data-separator-hours-minutes></span>
      <span data-countdown-minutes></span>
      <span data-countdown-minutes-label>${window.theme.strings.countdown.minutes}</span>
      <span data-separator-minutes-seconds></span>
      <span data-countdown-seconds></span>
      <span data-countdown-seconds-label>${window.theme.strings.countdown.seconds}</span>
    `
  }

  startTimer () {
    if (this.timerInterval) return // Already running
    const deadlineDate = this.parseDeadline(this.deadline)
    this.updateTimer(deadlineDate)
    this.timerInterval = setInterval(() => {
      this.updateTimer(deadlineDate)
    }, 1000)
  }

  parseDeadline (deadline) {
    const [date, time] = deadline.split('|')
    const [day, month, year] = date.split('/').map((part) => part.trim())
    const [hours, minutes, seconds] = time
      .split(':')
      .map((part) => part.trim())

    return new Date(
      `20${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`,
    )
  }

  updateTimer (deadlineDate) {
    const now = new Date()
    const timeRemaining = deadlineDate - now

    if (timeRemaining <= 0) {
      clearInterval(this.timerInterval)
      this.els.message.element.innerHTML = this.afterMessage
      this.countdownEls = null
      if (this.hideAfterEnd) {
        this.setAttribute('aria-hidden', 'true')
      }
      return
    }

    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24))
    const hours = Math.floor(
      (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    )
    const minutes = Math.floor(
      (timeRemaining % (1000 * 60 * 60)) / (1000 * 60),
    )
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000)

    if (!this.countdownEls) {
      const messageEl = this.els.message.element
      this.countdownEls = {
        days: messageEl.querySelector('[data-countdown-days]'),
        hours: messageEl.querySelector('[data-countdown-hours]'),
        minutes: messageEl.querySelector('[data-countdown-minutes]'),
        seconds: messageEl.querySelector('[data-countdown-seconds]'),
        daysLabel: messageEl.querySelector('[data-countdown-days-label]'),
        hoursLabel: messageEl.querySelector('[data-countdown-hours-label]'),
        minutesLabel: messageEl.querySelector('[data-countdown-minutes-label]'),
        secondsLabel: messageEl.querySelector('[data-countdown-seconds-label]'),
        separatorDaysHours: messageEl.querySelector(
          '[data-separator-days-hours]',
        ),
        separatorHoursMinutes: messageEl.querySelector(
          '[data-separator-hours-minutes]',
        ),
        separatorMinutesSeconds: messageEl.querySelector(
          '[data-separator-minutes-seconds]',
        ),
      }
    }
    const els = this.countdownEls

    els.days.textContent = days.toString().padStart(2, '0')
    els.hours.textContent = hours.toString().padStart(2, '0')
    els.minutes.textContent = minutes.toString().padStart(2, '0')
    els.seconds.textContent = seconds.toString().padStart(2, '0')

    const showDays = days > 0
    const showHours = showDays || hours > 0
    const showMinutes = showHours || minutes > 0

    els.days.dataset.show = showDays
    els.daysLabel.dataset.show = showDays
    els.separatorDaysHours.dataset.show = showDays
    els.hours.dataset.show = showHours
    els.hoursLabel.dataset.show = showHours
    els.separatorHoursMinutes.dataset.show = showHours
    els.minutes.dataset.show = showMinutes
    els.minutesLabel.dataset.show = showMinutes
    els.separatorMinutesSeconds.dataset.show = showMinutes

    els.separatorDaysHours.textContent = this.separator
    els.separatorHoursMinutes.textContent = this.separator
    els.separatorMinutesSeconds.textContent = this.separator

    els.daysLabel.textContent =
      days === 1
        ? window.theme.strings.countdown.day
        : window.theme.strings.countdown.days
    els.hoursLabel.textContent =
      hours === 1
        ? window.theme.strings.countdown.hour
        : window.theme.strings.countdown.hours
    els.minutesLabel.textContent =
      minutes === 1
        ? window.theme.strings.countdown.minute
        : window.theme.strings.countdown.minutes
    els.secondsLabel.textContent =
      seconds === 1
        ? window.theme.strings.countdown.second
        : window.theme.strings.countdown.seconds
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }

    this.countdownEls = null
  }
}

if (!customElements.get('countdown-message')) {
  customElements.define('countdown-message', CountdownMessage)
}
